import _ from "lodash";
import { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateQty } from "../modules/cart/actions";
import { fetchByDate } from "../modules/products/actions";
import {
  availableProductsById,
  currentDateSelector,
  menuReadySelector,
} from "../modules/products/selectors";
import { formatPrice } from "../utils/format";
import LoadingPlaceholder from "./loadingPlaceholder";
import FetchError from "./fetchError";
import ProductQuantityControls from "./productQuantityControls";
import CookingIllustration from "./cookingIllustration";
import {
  ProductAction,
  ProductCard,
  ProductCategory,
  ProductGrid,
  ProductImage,
  ProductInfo,
  ProductPrice,
  PromotedBadge,
  Products,
} from "./styles";

function ProductList({ categories }) {
  const dispatch = useDispatch();
  const products = useSelector(availableProductsById);
  const date = useSelector(currentDateSelector);
  const loading = useSelector((state) => state.products.loading);
  const error = useSelector((state) => state.products.error);
  const menuReady = useSelector(menuReadySelector);
  const productsByCategory = _.groupBy(products, "category.id");
  const cartQuantities = useSelector((state) => state.cart);

  function productCountLabel(count) {
    if (count === 1) return "1 pozycja";
    if (count > 1 && count < 5) return `${count} pozycje`;
    return `${count} pozycji`;
  }

  useEffect(() => {
    const request = dispatch(fetchByDate(date));
    return () => request.abort();
  }, [dispatch, date]);

  if (loading) {
    return <LoadingPlaceholder>Wczytywanie dań</LoadingPlaceholder>;
  }

  if (error) {
    return (
      <FetchError message={error} onRetry={() => dispatch(fetchByDate(date))} />
    );
  }

  if (menuReady && Object.keys(products).length === 0) {
    return (
      <section
        className="menu-empty"
        role="status"
        aria-labelledby="menu-empty-title"
      >
        <CookingIllustration />
        <h2 id="menu-empty-title" className="mb-1">Coś pysznego już się szykuje</h2>
        <p>Menu na wybrany dzień pojawi się już za chwilę</p>
      </section>
    );
  }

  return (
    <Products>
      {categories.map((category) => (
        <Fragment key={category.id}>
          <ProductCategory>
            <span>{category.name}</span>
            <small>
              {productCountLabel(
                (productsByCategory[category.id] || []).length,
              )}
            </small>
          </ProductCategory>
          <ProductGrid>
            {(productsByCategory[category.id] || []).map((product) => {
              const quantityInCart = cartQuantities[product.id] || 0;
              const inCart = quantityInCart > 0;

              return (
                <ProductCard key={product.id} $promoted={product.is_promoted}>
                  {product.photo && <ProductImage url={product.photo} />}
                  <ProductInfo>
                    {product.is_promoted && (
                      <PromotedBadge>Polecane</PromotedBadge>
                    )}
                    <strong>{product.name}</strong>
                    {product.description && <p>{product.description}</p>}
                    <div>
                      <ProductPrice>
                        {formatPrice(product.price)} zł
                      </ProductPrice>
                      {inCart ? (
                        <ProductQuantityControls
                          productId={product.id}
                          productName={product.name}
                          quantity={quantityInCart}
                        />
                      ) : (
                        <ProductAction
                          type="button"
                          onClick={() =>
                            dispatch(updateQty({ id: product.id, val: 1 }))
                          }
                        >
                          Dodaj
                        </ProductAction>
                      )}
                    </div>
                  </ProductInfo>
                </ProductCard>
              );
            })}
          </ProductGrid>
        </Fragment>
      ))}
    </Products>
  );
}

export default ProductList;
